import toast from "@/toast";

export interface BannerUpdate {
    type: 'ginfo' | 'gwarning' | 'gerror' | 'gsuccess';
    content: string;
}

export interface ViewUpdate {
    /** The id of an element on the page to update */
    id: string;
    /** The HTML content to update the view with */
    content: string;
    /** URL params to update in browser history (only specified params will be updated) */
    params?: Record<string, number|string|boolean>;
}

export interface JsonResponse {
    reload?: boolean;
    redirect?: string;
    alert?: string;
    banner?: BannerUpdate;
    success?: string;
    view?: ViewUpdate;
}

export interface JsonResponseHandlerDelegate { 
    onJsonResponse(response: JsonResponse): void;
}

/** Useful for indicating that a request succeeded, based on the response from the server */
export class JsonResponseHandler {
    public readonly delegates: JsonResponseHandlerDelegate[] = [];

    onResponse(response: Response): Promise<JsonResponse> {
        return response.json().then(data => {
            this.delegates.forEach(delegate => delegate.onJsonResponse(data));
            return data;
        });
    }
}

export class PageReloadHandler implements JsonResponseHandlerDelegate {
    onJsonResponse(response: JsonResponse): void {
        if(response.reload) window.location.reload();
    }
}

export class PageRedirectHandler implements JsonResponseHandlerDelegate {
    onJsonResponse(response: JsonResponse): void {
        if(response.redirect) window.location.href = response.redirect;
    }
}

export class BannerUpdateHandler implements JsonResponseHandlerDelegate {
    onJsonResponse(response: JsonResponse): void {
        if(response.banner) {
            this.updateBanner(response.banner);
        }
    }
    
    private updateBanner(data: BannerUpdate) {
        let banner = document.querySelector('.ww-banner') as HTMLElement;
        banner.classList.remove('ginfo', 'gwarning', 'gsuccess', 'gerror');
        banner.classList.add(data.type);
        banner.innerHTML = data.content;
    }
}

export class ViewUpdateHandler implements JsonResponseHandlerDelegate {
    onJsonResponse(response: JsonResponse): void {
        if (response.view) {
            this.updateView(response.view);
        }
    }

    private updateView(data: ViewUpdate): void {
        let view = document.getElementById(data.id);
        if (view) {
            view.innerHTML = data.content;
        }

        if (data.params) {
            const url = new URL(window.location.href);

            for (const [key, value] of Object.entries(data.params)) {
                url.searchParams.delete(key);
                if (value !== false) {
                    url.searchParams.set(key, String(value));
                }
            }

            const nextUrl = `${url.pathname}${url.search}`;
            history.pushState({ params: data.params }, '', nextUrl);
        }
    }
}

export class ToastSuccessHandler implements JsonResponseHandlerDelegate {
    onJsonResponse(response: JsonResponse): void {
        if(response.success) toast.success(response.success);
    }
}